import React, { useCallback, useMemo } from 'react'
import { ChevronRight, Heart, Star } from 'lucide-react'
import SaveButton from '../../../Components/SaveButton';
import Badge from '../../Components/Badge';
import { LogoIcon } from '../../../../header/Header';
import type { Restaurant } from '../../../Manager/restaurant.types';
import { useNavigate } from 'react-router-dom';

interface RanksProps {
    loading?: boolean;
    rankedCity?: {
      city: string;
      restaurants: Restaurant[];
    }
    onCardClick: (id: string, name: string) => void; 
};

const Ranks = ({ loading, rankedCity, onCardClick }: RanksProps) => {
  const navigate = useNavigate();
  const getRandomFoodTypes = (restaurant: Restaurant, max = 3) => {
    const randomLabels = useMemo(() => {
      const allLabels = [
        ...(restaurant.typeOfFood ?? []),
        ...(restaurant.experience ?? []),
        // ...(restaurant.tags ?? []),
        ...(restaurant.cuisine ?? []),
        // ...(restaurant.specialFeatures ?? []),
      ].filter(Boolean);

      if (allLabels.length === 0) return [];

      // Shuffle once, then pick 3
      return allLabels
        .sort(() => 0.5 - Math.random())
        .slice(0, 3);
    }, [restaurant._id]);

    return randomLabels.join(", ");
  };

  const handleSeeMore = useCallback(() => {
    navigate(
      `/cities/${rankedCity.city}/restaurants?name=${encodeURIComponent(
        rankedCity.city
      )}&page=1`
    );
  }, [navigate, rankedCity.city]);

  
  return (
      <div className="py-4">
        <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
          {/* Intro Card */}
          <div className="flex-shrink-0 w-72 h-72 rounded-2xl overflow-hidden shadow-2xl relative group cursor-pointer">
            {/* Base gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400" />
            
            {/* Artistic circular cuts - creating depth */}
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-gradient-to-br from-purple-600/40 to-transparent blur-2xl" />
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-gradient-to-tr from-orange-500/50 to-transparent blur-2xl" />
            <div className="absolute top-1/3 right-8 w-32 h-32 rounded-full bg-pink-400/30 blur-xl" />
            
            {/* Circular cut overlays with borders */}
            <div className="absolute top-12 right-16 w-40 h-40 rounded-full border-2 border-white/20 bg-white/5 backdrop-blur-sm" />
            <div className="absolute bottom-16 left-8 w-28 h-28 rounded-full border-2 border-white/15 bg-white/5 backdrop-blur-sm" />
            <div className="absolute top-24 left-12 w-20 h-20 rounded-full border border-white/25 bg-gradient-radial from-white/10 to-transparent" />
            
            {/* Small decorative circles */}
            <div className="absolute top-8 left-20 w-3 h-3 rounded-full bg-white/40" />
            <div className="absolute top-1/2 right-12 w-2 h-2 rounded-full bg-white/50" />
            <div className="absolute bottom-20 right-20 w-4 h-4 rounded-full bg-white/30" />
            
            {/* Overlay gradient for depth */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-transparent to-orange-500/20" />
            
            {/* Content */}
            <div className="relative z-10 h-full p-6 flex flex-col justify-end">
              <div className="transform transition-transform duration-300 group-hover:translate-y-[-4px]">
                <h3 className="flex items-center gap-1 text-white text-3xl font-bold mb-2 drop-shadow-lg">
                  Gemio
                  <LogoIcon size={5} />
                </h3>
                <p className="text-white text-xl font-semibold mb-1 drop-shadow-md">
                  Top {20} in {rankedCity?.city}
                </p>
                <p className="text-white/90 text-sm mb-3 drop-shadow-md">
                  According to our community
                </p>
                <div className="flex items-center gap-1 text-white font-medium group-hover:gap-2 transition-all duration-300">
                  <span className="drop-shadow-md">Let's go</span>
                  <ChevronRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
            </div>
            
            {/* Shine effect on hover */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-tr from-transparent via-white/10 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
          </div>

          {/* Ranked Restaurant Cards */}
          {rankedCity.restaurants.map((restaurant, idx) => (
            (() => {
              const metadataRating = typeof restaurant.googleMetadata?.rating === 'number'
                ? restaurant.googleMetadata.rating
                : null;
              const overallRating = metadataRating ?? restaurant.rating.average ?? 0;

              return (
            <div
            key={idx}
            className="relative flex-shrink-0 w-72 h-72 rounded-2xl overflow-hidden shadow-lg"
            onClick={() => onCardClick(restaurant._id, restaurant.name)}
            >
              {/* Badge */}
              <div className="absolute top-3 left-3 z-10">
                <Badge number={idx + 1} size={24} />
              </div>
              {/* Like Button */}
              <button className="absolute top-1 right-1 z-10">
                <SaveButton restaurantId={idx.toString()} stroke="white" />
              </button>
            {/* Background Image */}
            <img
                src={restaurant.gallery?.cover?.url}
                alt={restaurant.name}
                className="absolute inset-0 w-full h-full object-cover"
            />

            {/* Dark gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

            {/* Content */}
            <div className="relative z-10 flex flex-col justify-end h-full p-4 text-white">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg p-0 font-bold poppins-light">
                  {restaurant.name}
                  </h3>
                  <span className="flex items-center text-sm font-semibold">
                      <Star className="inline fill-[#ff4e1d] text-[#ff4e1d] poppins-extralight" size={12} />
                      {overallRating.toFixed(1)}
                  </span>
                </div>

                {/* Cuisine */}
                <p className="text-sm text-gray-200 mb-0.5">
                  {getRandomFoodTypes(restaurant)}
                </p>

                {/* Views */}
                <p className="text-xs text-gray-300">
                {restaurant.viewCount >= 1000
                    ? `${(restaurant.viewCount / 1000).toFixed(1)}k`
                    : restaurant.viewCount.toLocaleString()}{" "}
                views
                </p>
            </div>
            </div>
              );
            })()
          ))}

          <div className="flex flex-col items-center justify-center flex-shrink-0 w-30 rounded-2xl border-2 border-dashed border-gray-300 bg-white/50 hover:bg-white/50 transition-colors">
            <button className="p-4 bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-100 transition-colors flex items-center gap-2" onClick={handleSeeMore}>
              <ChevronRight className="w-4 h-4 text-gray-700" />
            </button>
            <span className="ml-2 text-sm font-medium text-gray-700">See More</span>
          </div>
        </div>
      </div>
  );
}

export default Ranks